import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api';
import { getHelpContextSignals } from '../../hooks/useHelpContextSignal';
import ChatMessage from '../ChatMessage';
import HelpArticleView from './HelpArticleView';
import HelpBrowseView from './HelpBrowseView';
import {
  startHelpRun,
  logHelpCitationsRendered,
  logHelpCitationClick,
} from './helpCitationDebug';
import './Help.css';

// Parse `/case/:caseId/:tab` for the active tab. Mirrors App.jsx routes.
const TAB_PATTERN = /^\/case\/[^/]+\/([a-z]+)/;
function deriveActiveTab(pathname) {
  const m = TAB_PATTERN.exec(pathname || '');
  if (!m) return null;
  const tab = m[1];
  return ['upload', 'checklist', 'inventory', 'exploration', 'settings'].includes(tab)
    ? tab
    : null;
}

/**
 * Parse a help citation URL ``/help/<slug>?anchor=<a>&q=<snip>`` into its
 * parts. Returns null when the href doesn't match (so the click handler
 * falls back to default link behavior). Accepts the legacy hash-prefixed
 * form ``#/help/<slug>...`` too, in case any persisted chat history still
 * carries pre-BrowserRouter URLs.
 */
function parseHelpHref(href) {
  if (!href || typeof href !== 'string') return null;
  const m = /^#?\/help\/([a-z0-9-]+)(?:\?([^#]*))?$/i.exec(href);
  if (!m) return null;
  const params = new URLSearchParams(m[2] || '');
  return {
    slug: m[1],
    anchor: params.get('anchor') || null,
    queryHighlight: params.get('q') || null,
  };
}

/**
 * In-app help drawer with a 3-view state machine.
 *
 *   "chat"     — message list + composer. Default view.
 *   "browse"   — categorized index of all help articles. Reached via
 *                "Browse help docs" in the chat header.
 *   "article"  — full article render. Reached via a chat citation or
 *                from the browse view.
 *
 * Navigation contract: the drawer maintains a small back-stack so the
 * back arrow returns the user to the right previous view. The chat
 * view is always kept MOUNTED (display:none under the other views) so
 * scroll position + composer text survive view changes without React
 * unmount churn.
 *
 * Citations in chat answers render as ``/help/<slug>?anchor=...&q=...``
 * markdown links — clicked in-drawer they push an article-view state
 * instead of navigating the SPA, so the user keeps their place in Arc
 * behind the drawer.
 *
 * Anyone who really wants the full standalone /help page clicks the
 * footer "Open full help page" link in the browse view — that route
 * has its own "← Back" affordance for getting back to the case.
 */
export default function HelpChatPanel({ onClose, initialScope = null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [escalateOpen, setEscalateOpen] = useState(false);

  // View stack — newest view on top. Always non-empty; bottom is 'chat'.
  // Stack entries are either 'chat', 'browse', or
  // { kind: 'article', slug, anchor, queryHighlight }.
  // initialScope.slug seeds an article frame on top of chat so right-click
  // and Cmd-/ open the relevant article immediately (back arrow returns
  // to chat).
  const [viewStack, setViewStack] = useState(() => {
    const base = [{ kind: 'chat' }];
    if (initialScope?.slug) {
      base.push({ kind: 'article', slug: initialScope.slug, anchor: null, queryHighlight: null });
    }
    return base;
  });
  const top = viewStack[viewStack.length - 1];
  const view = typeof top === 'string' ? top : top.kind;
  const articleView = view === 'article' ? top : null;

  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  // If initialScope carries a question (and no slug pre-loaded an article),
  // pre-fill the composer so the user just hits Send. With no slug or
  // question but a label, generate a generic ask-about-this prompt. Runs
  // once on mount.
  const initialScopeAppliedRef = useRef(false);
  useEffect(() => {
    if (initialScopeAppliedRef.current) return;
    if (!initialScope) return;
    initialScopeAppliedRef.current = true;
    if (initialScope.slug) return;  // article already pushed above
    if (initialScope.question) {
      setInput(initialScope.question);
    } else if (initialScope.label) {
      setInput(`Tell me about ${initialScope.label}.`);
    }
  }, [initialScope]);

  // Escape: pop the view stack (unless we're already at chat — then
  // let the global handler close the drawer if it's bound).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (viewStack.length > 1) {
        e.stopPropagation();
        setViewStack((s) => s.slice(0, -1));
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [viewStack.length]);

  const ensureConversation = useCallback(async () => {
    if (conversationId) return conversationId;
    const created = await api.post('/help/conversations', {});
    setConversationId(created.id);
    return created.id;
  }, [conversationId]);

  const buildAppState = useCallback(() => ({
    route: location.pathname,
    has_case_context: Boolean(params?.caseId || /\/case\//.test(location.pathname)),
    app_version: window.__APP_VERSION__ || null,
    active_tab: deriveActiveTab(location.pathname),
    // Component-pushed signals — see hooks/useHelpContextSignal.js. Strict UX
    // state only; nothing here may carry case content or identifiers.
    ...getHelpContextSignals(),
  }), [location, params]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;
    setError(null);
    setBusy(true);
    setInput('');

    // Optimistic user message
    const optimisticUser = { role: 'user', content: text, optimistic: true };
    setMessages((prev) => [...prev, optimisticUser]);

    try {
      const cid = await ensureConversation();
      startHelpRun({ conversationId: cid, message: text });

      const res = await api.post(`/help/conversations/${cid}/messages`, {
        message: text,
        app_state: buildAppState(),
      });

      const assistantMsg = {
        id: `asst-${Date.now()}`,
        role: 'assistant',
        content: res.answer || '',
        citations: res.citations || [],
        tool_trace: res.tool_trace || [],
        tokens_in: res.tokens_in,
        tokens_out: res.tokens_out,
        duration_ms: res.duration_ms,
        confidence: res.confidence,
      };
      setMessages((prev) => [
        ...prev.filter((m) => !m.optimistic),
        { id: `user-${Date.now()}`, role: 'user', content: text },
        assistantMsg,
      ]);

      logHelpCitationsRendered({
        citationCount: (res.citations || []).length,
        helpSlugs: (res.citations || []).map((c) => c.help_slug).filter(Boolean),
        durationMs: res.duration_ms,
        tokensIn: res.tokens_in,
        tokensOut: res.tokens_out,
      });
    } catch (e) {
      setError(e.message || 'Something went wrong. Try again.');
      setMessages((prev) => prev.filter((m) => !m.optimistic));
    } finally {
      setBusy(false);
    }
  }, [input, busy, ensureConversation, buildAppState]);

  const onComposerKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // ── View stack operations ────────────────────────────────────

  const goBack = () => setViewStack((s) => (s.length > 1 ? s.slice(0, -1) : s));

  const openBrowse = () => {
    // From chat: push browse. From elsewhere: replace with browse so we
    // don't accumulate stale article frames.
    setViewStack((s) => {
      if (s[s.length - 1] === 'browse' || s[s.length - 1]?.kind === 'browse') {
        return s;
      }
      return [...s, 'browse'];
    });
  };

  const openArticleFromCitation = (parsed) => {
    // Citations always launch from chat — push article on top of stack.
    setViewStack((s) => [...s, { kind: 'article', ...parsed }]);
  };

  const openArticleFromBrowse = (slug) => {
    setViewStack((s) => [...s, { kind: 'article', slug, anchor: null, queryHighlight: null }]);
  };

  // Slug links inside an article also push a new article frame, so the
  // user can drill in and back out without losing the previous article.
  const onArticleSlugLink = useCallback((targetSlug) => {
    setViewStack((s) => [...s, { kind: 'article', slug: targetSlug, anchor: null, queryHighlight: null }]);
  }, []);

  // ── Click-delegation handler ─────────────────────────────────
  // Citations in chat answers: keep them in-drawer.
  const onPanelClick = useCallback((e) => {
    const a = e.target.closest && e.target.closest('a.citation-link');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    const parsed = parseHelpHref(href);
    logHelpCitationClick({
      href,
      label: a.textContent,
      inDrawer: !!parsed,
    });
    if (parsed) {
      e.preventDefault();
      openArticleFromCitation(parsed);
    }
  }, []);

  const openFullPage = () => {
    // The escape hatch: close drawer + navigate to the standalone /help.
    // The standalone page has its own "← Back" link to return here.
    onClose();
    navigate('/help');
  };

  // Header label depends on the current view.
  let headerLabel;
  if (view === 'chat') headerLabel = 'Help';
  else if (view === 'browse') headerLabel = 'Help docs';
  else headerLabel = 'Help article';

  return (
    <>
      <div className="help-panel-backdrop" onClick={onClose} />
      <aside
        className="help-panel"
        role="dialog"
        aria-label={headerLabel}
        onClick={onPanelClick}
      >
        <header className="help-panel-header">
          {viewStack.length > 1 ? (
            <button
              className="help-panel-back"
              onClick={goBack}
              aria-label="Back"
            >
              ← Back
            </button>
          ) : (
            <div className="help-panel-title">Help</div>
          )}
          {view === 'chat' && (
            <a
              className="help-panel-browse"
              href="/help"
              onClick={(e) => { e.preventDefault(); openBrowse(); }}
            >
              Browse help docs
            </a>
          )}
          <button
            className="help-panel-close"
            onClick={onClose}
            aria-label="Close help"
          >
            ×
          </button>
        </header>

        {/* Chat view — kept mounted under other views so scroll +
            composer state survive without React unmount churn. */}
        <div
          className="help-panel-chat-wrapper"
          style={{ display: view === 'chat' ? 'flex' : 'none' }}
        >
          <div className="help-panel-body" ref={scrollRef}>
            {messages.length === 0 && !busy && (
              <div className="help-msg-empty">
                Ask a question about how Chargestack works.
                <br />
                I'll ground my answers in the help docs and cite the sections.
                <br />
                <a
                  className="help-quickstart-link"
                  href="https://chargestack.ai/quickstart/"
                  target="_blank"
                  rel="noreferrer"
                >
                  New here? Read the Quick Start Guide ↗
                </a>
              </div>
            )}
            {messages.map((m, i) => (
              <ChatMessage
                key={m.id || `msg-${i}`}
                msg={m}
                citations={m.citations}
                hideToolTrace
              />
            ))}
            {busy && <div className="help-msg-loading">Thinking…</div>}
            {error && <div className="help-msg-loading" style={{ color: 'var(--color-danger)' }}>{error}</div>}
          </div>

          {messages.some((m) => m.role === 'assistant') && (
            <div className="help-panel-actions">
              <button
                className="help-escalate-btn"
                onClick={() => setEscalateOpen(true)}
                disabled={busy}
              >
                Send to support
              </button>
            </div>
          )}

          <div className="help-panel-composer">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onComposerKeyDown}
              placeholder="Ask a question…"
              disabled={busy}
            />
            <button
              className="help-panel-send"
              onClick={send}
              disabled={busy || !input.trim()}
            >
              Send
            </button>
          </div>
        </div>

        {/* Browse view — categorized article index inside the drawer. */}
        {view === 'browse' && (
          <div className="help-panel-browse-view">
            <HelpBrowseView onArticleClick={openArticleFromBrowse} />
            <div className="help-panel-browse-footer">
              <a
                className="help-panel-open-full"
                href="https://chargestack.ai/quickstart/"
                target="_blank"
                rel="noreferrer"
              >
                Quick Start Guide ↗
              </a>
              <button
                className="help-panel-open-full"
                onClick={openFullPage}
              >
                Open full help page ↗
              </button>
            </div>
          </div>
        )}

        {/* Article view — same renderer as the standalone /help/<slug>. */}
        {articleView && (
          <div className="help-panel-article">
            <HelpArticleView
              slug={articleView.slug}
              anchor={articleView.anchor}
              queryHighlight={articleView.queryHighlight}
              viewedInDrawer={true}
              onSlugLink={onArticleSlugLink}
            />
          </div>
        )}
      </aside>

      {escalateOpen && (
        <EscalateModal
          conversationId={conversationId}
          appState={buildAppState()}
          onClose={() => setEscalateOpen(false)}
        />
      )}
    </>
  );
}


function EscalateModal({ conversationId, appState, onClose }) {
  const [summary, setSummary] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/help/tickets', {
        conversation_id: conversationId,
        summary: summary.trim() || null,
        context: appState,
      });
      setDone(true);
    } catch (e) {
      setError(e.message || 'Failed to send.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="help-modal-backdrop" onClick={onClose}>
      <div className="help-modal" onClick={(e) => e.stopPropagation()}>
        {done ? (
          <>
            <h3>Sent to support</h3>
            <p className="help-modal-success">
              We have your conversation and context. Someone will follow up.
            </p>
            <div className="help-modal-actions">
              <button className="help-modal-btn help-modal-btn-primary" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <h3>Send this conversation to support</h3>
            <p>
              Your full chat history and current context will be attached.
              Add a one-line summary if you'd like.
            </p>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="What were you trying to do? (optional)"
              maxLength={500}
            />
            {error && <p style={{ color: 'var(--color-danger)', marginTop: 8 }}>{error}</p>}
            <div className="help-modal-actions">
              <button className="help-modal-btn" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button
                className="help-modal-btn help-modal-btn-primary"
                onClick={submit}
                disabled={submitting}
              >
                {submitting ? 'Sending…' : 'Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
